import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Task } from '@amzn/awscat-pattern-library-service-model';
import { TiptapOutput } from '@amzn/awscat-editor';

import { ActionableWarningButton } from '../v2-common/ActionableWarningButton';
import ConfirmationModalView from '../v2-common/ConfirmationModalView';
import { scrollIntoComfortZone } from '../v2-common/tiptap-editor-integration/Window';
import { SortableRenderArgs } from '../v2-common/SortableList';
import { PatternCellField } from './PatternCellField';
import { PatternCellSkillEditor } from './PatternCellSkillEditor';
import { PatternCellActions } from './PatternCellActions';
import { PatternSuggestionPopover } from './PatternSuggestionPopover';
import { usePatternTaskViewModel } from './PatternTaskViewModel';
import styles from './PatternTask.module.scss';
import { useFilteredInlineEdits } from '../v2-hooks/useFilteredInlineEdits';
import { useSuggestionNavigationService } from '../../contexts/SuggestionNavigationServiceContext';
import { log } from '../../loggers/Logger';
import { InlineTextFilter } from '../../services/InlineTextOperations';
import { useFilteredInlineComments } from '../v2-hooks/useFilteredInlineComments';

/** Converts a viewport-relative DOMRect to page-absolute coordinates. */
function toPageRect(rect: DOMRect): DOMRect {
  return new DOMRect(rect.x + window.scrollX, rect.y + window.scrollY, rect.width, rect.height);
}

interface PatternTaskProps {
  readonly epicId: string;
  readonly task: Task;
  readonly sortableArgs: SortableRenderArgs;
  /** When true, the row mounts directly in edit mode (used for newly added tasks). */
  readonly initiallyEditing: boolean;
  readonly contentId: string;
}

/**
 * Renders one row in the tasks table — title, description, skills,
 * and actions. Each row manages its own editing lifecycle via
 * `usePatternTaskViewModel`.
 */
export const PatternTask = memo(function PatternTask({
  epicId,
  task,
  sortableArgs,
  initiallyEditing,
  contentId,
}: PatternTaskProps) {
  const { setNodeRef, style, dragHandleProps, isDragging } = sortableArgs;
  const vm = usePatternTaskViewModel(epicId, task, initiallyEditing);

  const titleButtonRef = useRef<HTMLButtonElement>(null);
  const descriptionButtonRef = useRef<HTMLButtonElement>(null);
  const [popoverTarget, setPopoverTarget] = useState<DOMRect | null>(null);

  // Delete modal is pure view state.
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const openDeleteModal = useCallback(() => setIsDeleteModalVisible(true), []);
  const dismissDeleteModal = useCallback(() => setIsDeleteModalVisible(false), []);
  const confirmDelete = useCallback(() => {
    vm?.deleteTask();
    setIsDeleteModalVisible(false);
  }, [vm]);

  // When the user types in the title cell.
  const handleTitleChange = useCallback(
    (output: TiptapOutput) => {
      vm?.stageFieldEdit('title', output.json);
    },
    [vm],
  );

  // When the user types in the description cell.
  const handleDescriptionChange = useCallback(
    (output: TiptapOutput) => {
      vm?.stageFieldEdit('description', output.json);
    },
    [vm],
  );

  // When inline edits/comments change, filter to only those targeting this task's title.
  const titleTextFilter = { type: 'epic_task_title', taskId: task.id } as const;
  const titleInlineEdits = useFilteredInlineEdits(titleTextFilter);
  const titleInlineComments = useFilteredInlineComments(titleTextFilter);

  const descriptionTextFilter = { type: 'epic_task_description', taskId: task.id } as const;
  const descriptionInlineEdits = useFilteredInlineEdits(descriptionTextFilter);
  const descriptionInlineComments = useFilteredInlineComments(descriptionTextFilter);

  const hasTitleSuggestions =
    titleInlineEdits.some((edit) => !edit.resolved) || titleInlineComments.length > 0;
  const hasDescriptionSuggestions =
    descriptionInlineEdits.some((edit) => !edit.resolved) || descriptionInlineComments.length > 0;

  // When this task has suggestions, report presence to the navigation service for next/prev traversal.
  const navigationService = useSuggestionNavigationService();

  // Report which task containers have navigable inline edits.
  // Only report in readonly mode — the inline-edit indicators only show in edit mode.
  useEffect(() => {
    if (!vm?.isEditing) {
      if (hasTitleSuggestions) {
        navigationService.report({ type: 'task_title_container', taskId: task.id, epicId });
      }
      if (hasDescriptionSuggestions) {
        navigationService.report({ type: 'task_description_container', taskId: task.id, epicId });
      }
    }
    return () => {
      navigationService.clear({ type: 'task_title_container', taskId: task.id });
      navigationService.clear({ type: 'task_description_container', taskId: task.id });
    };
  }, [
    navigationService,
    task.id,
    epicId,
    hasTitleSuggestions,
    hasDescriptionSuggestions,
    vm?.isEditing,
  ]);

  const showPopoverFor = useCallback((buttonRef: React.RefObject<HTMLButtonElement | null>) => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const pageRect = toPageRect(rect);
      scrollIntoComfortZone(pageRect);
      setPopoverTarget(pageRect);
    }
  }, []);

  // When navigation moves to an item in this task, show the suggestion popover.
  useEffect(() => {
    return navigationService.on('navigationItemChanged', ({ item }) => {
      if (item.type === 'task_title_container' && item.taskId === task.id) {
        showPopoverFor(titleButtonRef);
      } else if (item.type === 'task_description_container' && item.taskId === task.id) {
        showPopoverFor(descriptionButtonRef);
      } else {
        setPopoverTarget(null);
      }
    });
  }, [navigationService, task.id, showPopoverFor]);

  const dismissPopover = useCallback(() => {
    setPopoverTarget(null);
  }, []);
  const handlePopoverEdit = useCallback(() => {
    setPopoverTarget(null);
    vm?.startEditing();
  }, [vm]);

  // moveInto is a no-op if already at that position, so we always show the popover directly.
  const handleTitleButtonClick = useCallback(() => {
    navigationService.moveInto({ type: 'task_title_container', taskId: task.id });
    showPopoverFor(titleButtonRef);
  }, [navigationService, task.id, showPopoverFor]);

  const handleDescriptionButtonClick = useCallback(() => {
    navigationService.moveInto({ type: 'task_description_container', taskId: task.id });
    showPopoverFor(descriptionButtonRef);
  }, [navigationService, task.id, showPopoverFor]);

  // Declare what inline edits to show in the tiptap editor for each title/description
  // field, memorized to prevent tiptap from re-rendering
  const taskTitleFilter: InlineTextFilter = useMemo(() => {
    return {
      type: 'epic_task_title',
      taskId: task.id,
    };
  }, [task.id]);
  const taskDescFilter: InlineTextFilter = useMemo(() => {
    return {
      type: 'epic_task_description',
      taskId: task.id,
    };
  }, [task.id]);

  if (!vm) return null;

  const rowClassName = isDragging
    ? styles.draggingRow
    : vm.isEditing
      ? styles.editingRow
      : undefined;

  return (
    <tr ref={setNodeRef} style={style} className={rowClassName}>
      <td className={styles.cell}>
        <div className={hasTitleSuggestions && !vm.isEditing ? styles.cellWithWarning : undefined}>
          {hasTitleSuggestions && !vm.isEditing && (
            <ActionableWarningButton
              ref={titleButtonRef}
              ariaLabel="AI suggestions available for title"
              onClick={handleTitleButtonClick}
            />
          )}
          <PatternCellField
            initialValue={task.title}
            isReadonly={!vm.isEditing}
            onChange={handleTitleChange}
            isAutoFocused={vm.isEditing}
            contentId={contentId}
            inlineTextFilter={taskTitleFilter}
          />
        </div>
      </td>
      <td className={styles.cell}>
        <div
          className={
            hasDescriptionSuggestions && !vm.isEditing ? styles.cellWithWarning : undefined
          }
        >
          {hasDescriptionSuggestions && !vm.isEditing && (
            <ActionableWarningButton
              ref={descriptionButtonRef}
              ariaLabel="AI suggestions available for description"
              onClick={handleDescriptionButtonClick}
            />
          )}
          <PatternCellField
            initialValue={task.description}
            isReadonly={!vm.isEditing}
            onChange={handleDescriptionChange}
            contentId={contentId}
            inlineTextFilter={taskDescFilter}
          />
        </div>
      </td>
      <td className={styles.cell}>
        <PatternCellSkillEditor
          skills={task.skills}
          isReadonly={!vm.isEditing}
          onSkillsChange={vm.stageSkillsEdit}
        />
      </td>
      <td className={styles.actionsCell}>
        <PatternCellActions
          isEditing={vm.isEditing}
          onEditClick={vm.startEditing}
          onSaveClick={vm.saveEditing}
          onCancelClick={vm.cancelEditing}
          onDeleteClick={openDeleteModal}
          dragHandleProps={dragHandleProps}
          isDragging={isDragging}
        />

        {isDeleteModalVisible && (
          <ConfirmationModalView
            header="Delete this story?"
            body="The story will be removed from the epic. This cannot be undone."
            confirmLabel="Delete"
            isConfirming={false}
            onConfirm={confirmDelete}
            onDismiss={dismissDeleteModal}
          />
        )}
      </td>

      {popoverTarget && (
        <PatternSuggestionPopover
          target={popoverTarget}
          onClose={dismissPopover}
          onEdit={handlePopoverEdit}
        />
      )}
    </tr>
  );
});
